import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';

const useStyles = makeStyles(() => ({
  container: {
    padding: 20,
    maxWidth: 900,
    margin: '0 auto',
    textAlign: 'left',
  },
  section: {
    marginTop: 18,
  },
  monoBlock: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 13,
    padding: 14,
    borderRadius: 10,
    background: '#151922',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.9)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: 10,
  },
  openApiPanel: {
    marginTop: 24,
    padding: 15,
    borderRadius: 10,
    background: 'rgba(80, 227, 194, 0.10)',
    border: '1px solid rgba(80, 227, 194, 0.35)',
  },
  openApiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    marginTop: 10,
  },
  openApiCard: {
    padding: 12,
    borderRadius: 8,
    background: 'rgba(0, 0, 0, 0.22)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  openApiLabel: {
    color: '#ffffff',
    margin: '0 0 6px 0',
    fontWeight: 'bold',
  },
  openApiText: {
    color: '#a0a0a0',
    margin: 0,
    fontSize: '0.9em',
  },
  openApiBlock: {
    marginTop: 12,
    padding: 14,
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.10)',
    background: '#0e1117',
    color: '#d7e0ff',
    overflowX: 'auto',
    whiteSpace: 'pre',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 12,
    lineHeight: 1.5,
  },
  openApiEndpointList: {
    margin: '10px 0 0 0',
    paddingLeft: 18,
    color: 'rgba(255,255,255,0.82)',
  },
  openApiMethod: {
    display: 'inline-block',
    minWidth: 56,
    marginRight: 8,
    padding: '2px 8px',
    borderRadius: 999,
    background: 'rgba(77, 121, 255, 0.20)',
    border: '1px solid rgba(77, 121, 255, 0.40)',
    color: '#dce5ff',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
  },
  backButton: {
    color: '#dce5ff',
    borderColor: 'rgba(77, 121, 255, 0.70)',
    background: 'rgba(77, 121, 255, 0.14)',
    '&:hover': {
      borderColor: 'rgba(77, 121, 255, 0.95)',
      background: 'rgba(77, 121, 255, 0.26)',
    },
  },
}));

export default function ApiGuidePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  const [openApiLoading, setOpenApiLoading] = useState(false);
  const [openApiErr, setOpenApiErr] = useState('');
  const [openApiSpec, setOpenApiSpec] = useState(null);

  const descStyle = { color: 'rgba(255,255,255,0.75)' };

  const base = String(SERVER_NAME || '').replace(/\/$/, '').replace(/\/api\/?$/, '');
  const storiesUrl = `${base}/api/stories`;
  const agentsUrl = `${base}/api/agents`;
  const agentsStatsUrl = `${base}/api/agents/stats`;
  const agentDetailsUrl = `${base}/api/agents/0xYOUR_TOKEN_ADDRESS`;
  const exampleKey = 'YOUR_API_KEY';

  const curlExample = `curl -sS '${storiesUrl}?limit=25&offset=0' \\
  -H 'Accept: application/json' \\
  -H 'x-api-key: ${exampleKey}'`;

  const curlAgentsExample = `curl -sS '${agentsUrl}?limit=25&offset=0' \\
  -H 'Accept: application/json' \\
  -H 'x-api-key: ${exampleKey}'`;

  const curlAgentDetailsExample = `curl -sS '${agentDetailsUrl}' \\
  -H 'Accept: application/json' \\
  -H 'x-api-key: ${exampleKey}'`;

  const curlAgentsStatsExample = `curl -sS '${agentsStatsUrl}' \\
  -H 'Accept: application/json' \\
  -H 'x-api-key: ${exampleKey}'`;

  const openApiUrl = `${base}/openapi`;
  const openApiPretty = openApiSpec ? JSON.stringify(openApiSpec, null, 2) : '';
  const openApiPaths = openApiSpec && openApiSpec.paths && typeof openApiSpec.paths === 'object'
    ? Object.entries(openApiSpec.paths).reduce((acc, [path, methods]) => {
      const methodList = methods && typeof methods === 'object'
        ? Object.keys(methods).filter((method) => typeof methods[method] === 'object').sort()
        : [];
      if (methodList.length > 0) acc.push({ path, methods: methodList });
      return acc;
    }, []).sort((a, b) => a.path.localeCompare(b.path))
    : [];
  const openApiSummary = openApiSpec
    ? {
      title: openApiSpec.info && openApiSpec.info.title ? String(openApiSpec.info.title) : 'OpenAPI Schema',
      version: openApiSpec.openapi ? String(openApiSpec.openapi) : 'unknown',
      description: openApiSpec.info && openApiSpec.info.description ? String(openApiSpec.info.description) : '',
      pathCount: openApiPaths.length,
      serverCount: Array.isArray(openApiSpec.servers) ? openApiSpec.servers.length : 0,
    }
    : null;

  const copyText = useCallback(async (text) => {
    const t = String(text || '');
    if (!t) return;
    try {
      if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(t);
        return;
      }
    } catch (e) {
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
    }
  }, []);

  const onCopyOpenApi = useCallback(async () => {
    return copyText(openApiPretty);
  }, [copyText, openApiPretty]);

  const loadOpenApi = useCallback(async () => {
    setOpenApiLoading(true);
    setOpenApiErr('');
    try {
      const resp = await fetch(openApiUrl, { credentials: 'include' });
      const data = await resp.json();
      if (!resp.ok) throw new Error('Failed to load OpenAPI schema');
      setOpenApiSpec(data && typeof data === 'object' ? data : null);
    } catch (e) {
      setOpenApiSpec(null);
      setOpenApiErr('Unable to load the OpenAPI schema right now.');
    } finally {
      setOpenApiLoading(false);
    }
  }, [openApiUrl]);

  useEffect(() => {
    loadOpenApi();
  }, [loadOpenApi]);

  return (
    <div className={classes.container}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Typography variant="h5" style={{ color: '#ffffff' }}>API Guide</Typography>
          <Button variant="outlined" color="primary" className={classes.backButton} onClick={() => navigate('/api')}>Back to API Keys</Button>
        </div>

        <div className={classes.section}>
          <Typography variant="h6" style={{ color: '#ffffff' }}>Authentication</Typography>
          <Typography variant="body2" style={descStyle}>
            For API access, include your key in the request header: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>x-api-key</span>.
          </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
            The E3D web client can call these endpoints without an API key. If you provide <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>x-api-key</span>, requests are attributed to your key and subject to tier limits.
          </Typography>
        </div>

        <div className={classes.section}>
          <Typography variant="h6" style={{ color: '#ffffff' }}>Tiers &amp; Limits</Typography>
          <Typography variant="body2" style={descStyle}>
            Limits are enforced per API key.
          </Typography>
        <div className={classes.monoBlock}>
          {`Free:\n- Daily limit: 100 requests/day\n- Minimum interval: 5 seconds between requests\n\nPremium:\n- Daily limit: 1000 requests/day\n- Minimum interval: 1 second between requests`}
        </div>
        </div>

        <div className={classes.section}>
          <Typography variant="h6" style={{ color: '#ffffff' }}>/api/stories</Typography>
          <Typography variant="body2" style={descStyle}>
          Fetch on-chain stories for display or downstream indexing. The response is JSON containing a list of stories and associated metadata.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          Common query params include <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>limit</span> and <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>offset</span>. Use smaller limits for faster iteration during development.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          Errors you may see:
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 401</span> (missing/invalid key),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 429</span> (rate limit / daily quota),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 503</span> (database unavailable).
        </Typography>
        <div className={classes.monoBlock}>{curlExample}</div>
        </div>

        <div className={classes.section}>
          <Typography variant="h6" style={{ color: '#ffffff' }}>/api/agents</Typography>
          <Typography variant="body2" style={descStyle}>
          List agent tokens and their current status. This is the endpoint behind the Agents directory and is typically the best starting point if you want to discover token addresses to query in more detail.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          Supports paging with <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>limit</span> and <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>offset</span>. Optional params:
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> sort</span>,
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> dir</span>,
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> includeFunding</span>.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          Errors you may see:
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 401</span> (missing/invalid key when providing a key),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 429</span> (rate limit / daily quota),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 500</span> (server error).
        </Typography>
        <div className={classes.monoBlock}>{curlAgentsExample}</div>
        </div>

        <div className={classes.section}>
          <Typography variant="h6" style={{ color: '#ffffff' }}>/api/agents/:tokenAddress</Typography>
          <Typography variant="body2" style={descStyle}>
          Get details for a single agent token. Use this for a token detail page or for monitoring a specific token over time.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          The response includes status plus additional sections such as recent activities and funding stats when available.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          Errors you may see:
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 400</span> (invalid token address),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 401</span> (missing/invalid key when providing a key),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 429</span> (rate limit / daily quota).
        </Typography>
        <div className={classes.monoBlock}>{curlAgentDetailsExample}</div>
        </div>

        <div className={classes.section}>
          <Typography variant="h6" style={{ color: '#ffffff' }}>/api/agents/stats</Typography>
          <Typography variant="body2" style={descStyle}>
          Returns counts of agents by status. Useful for dashboards and quick health checks.
        </Typography>
          <Typography variant="body2" style={{ ...descStyle, marginTop: 10 }}>
          Errors you may see:
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 401</span> (missing/invalid key when providing a key),
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}> 429</span> (rate limit / daily quota).
        </Typography>
        <div className={classes.monoBlock}>{curlAgentsStatsExample}</div>
        </div>

        <div className={classes.openApiPanel}>
          <Typography variant="h6" style={{ color: '#ffffff', marginTop: 0, display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>📘</span>
            /api/openapi
          </Typography>
          <Typography variant="body2" style={descStyle}>
            This endpoint returns the service OpenAPI schema in JSON format. Use it to discover available endpoints, methods, and response shapes.
          </Typography>

          {openApiLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" style={descStyle}>Loading OpenAPI schema…</Typography>
            </div>
          ) : openApiErr ? (
            <Typography variant="body2" style={{ color: '#ff9b9b', marginTop: 12 }}>
              {openApiErr}
            </Typography>
          ) : openApiSummary ? (
            <>
              <div className={classes.openApiGrid}>
                <div className={classes.openApiCard}>
                  <p className={classes.openApiLabel}>Schema title</p>
                  <p className={classes.openApiText}>{openApiSummary.title}</p>
                </div>
                <div className={classes.openApiCard}>
                  <p className={classes.openApiLabel}>OpenAPI version</p>
                  <p className={classes.openApiText}>{openApiSummary.version}</p>
                </div>
                <div className={classes.openApiCard}>
                  <p className={classes.openApiLabel}>Paths</p>
                  <p className={classes.openApiText}>{openApiSummary.pathCount}</p>
                </div>
                <div className={classes.openApiCard}>
                  <p className={classes.openApiLabel}>Servers</p>
                  <p className={classes.openApiText}>{openApiSummary.serverCount}</p>
                </div>
              </div>

              {openApiSummary.description && (
                <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.75)', marginTop: 12 }}>
                  {openApiSummary.description}
                </Typography>
              )}

              <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.75)', marginTop: 12 }}>
                Schema URL: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{openApiUrl}</span>
              </Typography>

              <div className={classes.openApiBlock}>{openApiPretty}</div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                <Button variant="outlined" color="primary" href={openApiUrl} target="_blank" rel="noreferrer">
                  Open schema JSON
                </Button>
                <Button variant="outlined" color="primary" onClick={onCopyOpenApi}>
                  Copy JSON
                </Button>
              </div>

              <Typography variant="subtitle2" style={{ color: '#ffffff', marginTop: 16 }}>
                Sample endpoints
              </Typography>
              <ul className={classes.openApiEndpointList}>
                {openApiPaths.slice(0, 10).map((entry) => (
                  <li key={entry.path} style={{ marginBottom: 6 }}>
                    <span className={classes.openApiMethod}>{entry.methods.join(' / ').toUpperCase()}</span>
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{entry.path}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Typography variant="body2" style={descStyle}>
              OpenAPI schema data is not available yet.
            </Typography>
          )}
        </div>
    </div>
  );
}
